/**
 * Ship component types.
 *
 * Components occupy surface area inside a compartment. They have no health of
 * their own — the only way to lose one is to have the compartment around it
 * broken open, leaving it floating in space (see geometry/compartments.ts).
 */
export const COMPONENT_TYPES = [
    'engine',
    'turretSmall',
    'thrusterSmall',
    'thruster',
    'thrusterLarge',
    'landingGear',
    'collector',
    'shield',
    'reactor',
    'vault',
    'pilotSeat',
    'scanner',
];
/**
 * TODO(balance): every `footprint` here is a placeholder 1, and the produce /
 * consume rates are first-pass values. They want playtesting, not guessing.
 *
 * The one figure that is not arbitrary: an engine supplies 3 power and a
 * thruster draws 1, which makes the design doc's own example literally true —
 * three engines (9 power) cannot run twelve thrusters (12 demand) at full
 * capacity, they run at 75%.
 */
export const COMPONENT_SPECS = {
    engine: {
        weight: 5,
        type: 'engine',
        name: 'Engine',
        footprint: 1,
        mounting: 'internal',
        model: 'small_engine',
        required: true,
        upgradeable: true,
        produces: { power: 8 },
        consumes: {},
        description: 'Generates power. Total engine output caps how many thrusters can run at full capacity.',
    },
    turretSmall: {
        weight: 2,
        type: 'turretSmall',
        // The id stays `weapon`: it is written into every saved design, and
        // renaming it would orphan every hull anyone has drawn. Only the label
        // moved, and the label is the part players read.
        name: 'Fixed Small Turret',
        footprint: 1,
        mounting: 'edge',
        mountHeight: 'middle',
        model: 'turret',
        required: false,
        upgradeable: true,
        produces: {},
        consumes: { energy: 2 },
        description: 'Attacks other ships, stations, and asteroids. Draws energy.',
    },
    /**
     * Three tiers of the same casting.
     *
     * Small ones are for docking and fine positioning and stay available on every
     * ship; the larger two are what actually move a hull. Flight will let you
     * switch between the small set and the main set, so a ship carries both for
     * different jobs rather than compromising on one size.
     *
     * TODO(balance): the power figures are placeholders scaled off the small one.
     * They want tuning against real flight, not guessing.
     */
    thrusterSmall: {
        weight: 1,
        type: 'thrusterSmall',
        name: 'Small Thruster',
        footprint: 1,
        mounting: 'edge',
        mountHeight: 'middle',
        model: 'thruster',
        modelScale: 1,
        required: true,
        upgradeable: true,
        produces: {},
        consumes: { power: 1 },
        description: 'Precise, weak. For docking and fine positioning.',
    },
    thruster: {
        weight: 1.5,
        type: 'thruster',
        name: 'Thruster',
        footprint: 1,
        mounting: 'edge',
        mountHeight: 'middle',
        model: 'thruster',
        modelScale: 2,
        required: false,
        upgradeable: true,
        produces: {},
        consumes: { power: 4 },
        description: 'The standard drive. Much more powerful than a small thruster.',
    },
    thrusterLarge: {
        weight: 5,
        type: 'thrusterLarge',
        name: 'Large Thruster',
        footprint: 1,
        mounting: 'edge',
        mountHeight: 'middle',
        model: 'thruster',
        modelScale: 5,
        required: false,
        upgradeable: true,
        produces: {},
        consumes: { power: 12 },
        description: 'For very large hulls. Enormous, and thirsty.',
    },
    landingGear: {
        // Not in the weight table this came from; sized against its neighbours
        // and flagged so it is a decision waiting to be made rather than a
        // number that quietly appeared.
        weight: 2,
        type: 'landingGear',
        name: 'Landing Gear',
        footprint: 1,
        mounting: 'internal',
        model: 'landinggear_inactive',
        modelDeployed: 'landinggear_active',
        required: false,
        upgradeable: false,
        produces: {},
        consumes: {},
        description: 'Required to land at a compatible station pad. Mounts inside the hull but hangs below it, so it is one of the few parts visible from outside in flight.',
    },
    collector: {
        weight: 2,
        type: 'collector',
        name: 'Collector',
        footprint: 1,
        mounting: 'edge',
        mountHeight: 'raised',
        model: 'collector',
        required: false,
        upgradeable: true,
        produces: {},
        consumes: { energy: 1 },
        description: 'Collects resources from asteroid fields and anomalies. Draws energy.',
    },
    shield: {
        // Not in the weight table this came from; sized against its neighbours
        // and flagged so it is a decision waiting to be made rather than a
        // number that quietly appeared.
        weight: 5,
        type: 'shield',
        name: 'Shield',
        footprint: 1,
        mounting: 'internal',
        model: null,
        required: false,
        upgradeable: true,
        produces: {},
        consumes: { energy: 2 },
        description: 'Reduces incoming damage to lines. Draws energy.',
    },
    reactor: {
        weight: 5,
        type: 'reactor',
        name: 'Reactor',
        footprint: 1,
        mounting: 'internal',
        model: null,
        required: false,
        upgradeable: true,
        produces: { energy: 5 },
        consumes: {},
        description: 'Generates energy for weapons, shields, scanners and collectors.',
    },
    vault: {
        weight: 8,
        type: 'vault',
        name: 'Vault',
        footprint: 1,
        mounting: 'internal',
        model: 'vault',
        cargoCapacity: 20,
        required: false,
        upgradeable: true,
        produces: {},
        consumes: {},
        description: 'Holds 20 weight of cargo. A ship carries the sum of its vaults.',
    },
    pilotSeat: {
        weight: 8,
        type: 'pilotSeat',
        name: 'Pilot Seat',
        footprint: 1,
        mounting: 'internal',
        model: 'pilot_seat',
        required: true,
        upgradeable: false,
        produces: {},
        consumes: {},
        description: 'First-person camera location, and what lets a ship be controlled at all.',
    },
    scanner: {
        weight: 2,
        type: 'scanner',
        name: 'Scanner',
        footprint: 1,
        mounting: 'internal',
        model: null,
        required: false,
        upgradeable: true,
        produces: {},
        consumes: { energy: 1 },
        description: 'Scans anomalies, stations, and planets. Draws energy.',
    },
};
/**
 * The two thruster sets, which are alternates rather than additions.
 *
 * A ship runs one or the other, never both: precision thrusters are for docking
 * and fine positioning, the main set is for going somewhere. Switching between
 * them is a flight control, so a hull's power demand is the *larger* of the two
 * sets — never their sum, which would have every ship budgeting for a state it
 * can never be in.
 */
export const PRECISION_THRUSTERS = ['thrusterSmall'];
export const MAIN_THRUSTERS = ['thruster', 'thrusterLarge'];
export const THRUSTER_TYPES = [
    ...PRECISION_THRUSTERS,
    ...MAIN_THRUSTERS,
];
export function isThruster(type) {
    return THRUSTER_TYPES.includes(type);
}
export function thrusterSet(type) {
    if (PRECISION_THRUSTERS.includes(type))
        return 'precision';
    if (MAIN_THRUSTERS.includes(type))
        return 'main';
    return null;
}
/** What one set of thrusters draws, and therefore how hard it pushes. */
export function thrustOf(componentTypes, set) {
    return componentTypes
        .filter((type) => thrusterSet(type) === set)
        .reduce((total, type) => total + (COMPONENT_SPECS[type].consumes.power ?? 0), 0);
}
/** Component types a ship cannot fly without: at least one pilot seat, engine, and thruster. */
export const REQUIRED_COMPONENT_TYPES = COMPONENT_TYPES.filter((type) => COMPONENT_SPECS[type].required);
/**
 * Does this set of parts cover everything a ship needs?
 *
 * Not a plain subset check, because "a thruster" means any tier — a hull driven
 * by two large thrusters and nothing else is obviously flyable, and an earlier
 * version that demanded the small one specifically would have grounded it.
 */
export function meetsRequirements(componentTypes) {
    const hasThruster = componentTypes.some(isThruster);
    return REQUIRED_COMPONENT_TYPES.every((required) => isThruster(required) ? hasThruster : componentTypes.includes(required));
}
/**
 * Tally one resource across a set of components.
 *
 * Only counts components that are actually running: an inactive internal
 * thruster draws nothing, and a destroyed one is not in the list at all.
 */
export function computeResourceBudget(componentTypes, kind) {
    let produced = 0;
    let demanded = 0;
    for (const type of componentTypes) {
        produced += COMPONENT_SPECS[type].produces[kind] ?? 0;
        // Thrusters are counted per set below, not here: only one set runs at a
        // time, so adding both would budget for a state the ship can never be in.
        if (kind === 'power' && isThruster(type))
            continue;
        demanded += COMPONENT_SPECS[type].consumes[kind] ?? 0;
    }
    if (kind === 'power') {
        demanded += Math.max(thrustOf(componentTypes, 'precision'), thrustOf(componentTypes, 'main'));
    }
    return {
        kind,
        produced,
        demanded,
        satisfaction: demanded <= 0 ? 1 : Math.min(1, produced / demanded),
    };
}
/**
 * Derive a ship's performance from the components currently running.
 *
 * Pass only active components — see `Compartment.exposed` for why an internal
 * thruster may or may not be among them.
 */
export function computeShipPerformance(componentTypes) {
    const power = computeResourceBudget(componentTypes, 'power');
    const energy = computeResourceBudget(componentTypes, 'energy');
    const thrust = {
        precision: thrustOf(componentTypes, 'precision'),
        main: thrustOf(componentTypes, 'main'),
    };
    const flyable = meetsRequirements(componentTypes);
    // A hull with only precision thrusters still moves, just barely — which is
    // a real (if poor) design rather than a broken one.
    const travelling = thrust.main > 0 ? thrust.main : thrust.precision;
    return {
        power,
        energy,
        maxSpeed: flyable ? travelling * power.satisfaction : 0,
        thrust,
        flyable,
    };
}
//# sourceMappingURL=components.js.map